import { Component, type ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("ErrorBoundary caught:", error, info.componentStack);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center min-h-[60vh] px-6 text-center">
          <div className="w-16 h-16 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center mb-6">
            <svg
              className="w-8 h-8 text-red-500 dark:text-red-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h2
            className="text-slate-900 dark:text-white mb-3"
            style={{ fontSize: "1.5rem", fontWeight: 700 }}
          >
            Something went wrong
          </h2>
          <p
            className="text-slate-500 dark:text-slate-400 mb-6 max-w-md"
            style={{ fontSize: "1rem", lineHeight: 1.6 }}
          >
            An unexpected error occurred while loading this page. Please try
            refreshing, or head back to the homepage.
          </p>
          <div className="flex gap-3">
            <button
              onClick={() => {
                this.setState({ hasError: false, error: null });
                window.location.reload();
              }}
              className="px-5 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white transition-colors"
              style={{ fontWeight: 600, fontSize: "0.9375rem" }}
            >
              Refresh page
            </button>
            {/*
              Plain anchor — NOT react-router <Link>. This ErrorBoundary is
              mounted ABOVE <RouterProvider> in App.tsx, so a <Link> here has
              no router context and throws, blanking the whole page and masking
              the real error. A full-navigation <a> always works.
            */}
            <a
              href="/"
              className="px-5 py-2.5 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
              style={{ fontWeight: 600, fontSize: "0.9375rem" }}
            >
              Go home
            </a>
          </div>

          {/* Dev-only error detail to aid debugging */}
          {import.meta.env.DEV && this.state.error && (
            <pre
              className="mt-6 max-w-2xl overflow-auto rounded-xl bg-slate-900 p-4 text-left text-xs text-red-300"
              style={{ whiteSpace: "pre-wrap" }}
            >
              {this.state.error.message}
              {"\n\n"}
              {this.state.error.stack}
            </pre>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}
